import { useState } from 'react';
import { CheckIcon, CopyIcon } from 'lucide-react';

import { cn } from '@/shadcn/lib/utils';
import { Button } from '@/shadcn/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';
import { TestId } from '@/types/testId';

type Props = {
  textToCopy: string;
  tooltipText?: string;
  onClick?: () => void;
};

export const Copy = ({ textToCopy, onClick, tooltipText }: Props) => {
  const [copied, setCopied] = useState<boolean>(false);

  const handleCopy = async () => {
    try {
      if (!navigator?.clipboard) {
        console.warn(
          'Clipboard is not supported in non secure environment. Example: Local host.'
        );
        return;
      }

      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      onClick?.();
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="smallIcon"
            className="disabled:opacity-100"
            onClick={handleCopy}
            aria-label={copied ? 'Copied' : 'Copy to clipboard'}
            disabled={copied}
            data-testid={TestId.Copy.Button}
          >
            <div
              className={cn(
                'transition-all',
                copied ? 'scale-100 opacity-100' : 'scale-0 opacity-0'
              )}
            >
              <CheckIcon className="stroke-emerald-500" aria-hidden="true" />
            </div>
            <div
              className={cn(
                'absolute transition-all',
                copied ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
              )}
            >
              <CopyIcon aria-hidden="true" />
            </div>
          </Button>
        </TooltipTrigger>
        <TooltipContent className="px-2 py-1 text-xs">
          {tooltipText || 'Click to copy'}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
